import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  IoConstruct,
  IoCreate,
  IoHeart,
  IoInformationCircle,
  IoStatsChart,
  IoDocumentText,
  IoMedal,
  IoFlask,
  IoHeartOutline,
  IoMusicalNotes,
  IoNewspaper,
  IoBulb,
  IoCalendar,
  IoSchool,
  IoTime,
  IoEye,
  IoDownload,
  IoCheckmarkCircle,
  IoCloseCircle,
  IoArrowForward,
  IoEllipsisVertical,
  IoFileTray,
  IoAdd,
} from 'react-icons/io5';
import { useUniversityData } from '@/services/universityService';
import { useDraftVersionsByUniversity } from '@/services/draftService';
import { API } from '@/utils/api.utils';
import api from '@/utils/api.utils';
import { User } from '@/types';
import './Applications.css';

interface University {
  university_id: number;
  user_id: number;
  status: string;
  is_suggested: boolean;
  id: number;
  university_name: string;
}

interface UniversityResponse {
  IN_PROGRESS: {
    universities: University[];
    count: number;
  };
  PENDING: {
    universities: University[];
    count: number;
  };
  COMPLETED: {
    universities: University[];
    count: number;
  };
}

interface CollegeData {
  university_id: number;
  name: string;
  deadline: string;
  status: string;
  statusColor: string;
  daysLeft: string;
  daysLeftColor: string;
  iconColor: string;
  iconTextColor: string;
  progress: number;
  interestStatus: string;
  interestStatusColor: string;
  interestDemonstrated: boolean;
  interestInfo: string;
  interestInfoColor: string;
  essayTitle: string;
}

interface ApplicationsProps {
  user?: User | null;
}

const Applications: React.FC<ApplicationsProps> = ({ user }) => {
  const [selectedCollegeId, setSelectedCollegeId] = useState<number | null>(null);

  // Fetch user universities
  const { data: universitiesData, isLoading: universitiesLoading } = useQuery<UniversityResponse>({
    queryKey: ['user-universities'],
    queryFn: async () => {
      const response = await api.get(API.USER_UNIVERSITIES);
      return response.data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  // Get all universities in a flat array
  const allUniversities = useMemo(() => {
    if (!universitiesData) return [];
    
    return [
      ...universitiesData.IN_PROGRESS.universities,
      ...universitiesData.PENDING.universities,
      ...universitiesData.COMPLETED.universities,
    ];
  }, [universitiesData]);

  // Set default selected college to the first one
  React.useEffect(() => {
    if (allUniversities.length > 0 && selectedCollegeId === null) {
      setSelectedCollegeId(allUniversities[0].university_id);
    }
  }, [allUniversities, selectedCollegeId]);

  // Fetch university details for selected college
  const { data: universityDetails } = useUniversityData(selectedCollegeId || 0);

  // Fetch drafts for selected college
  const { data: drafts = [] } = useDraftVersionsByUniversity(selectedCollegeId || 0, undefined, !!selectedCollegeId);


  // Get only the latest version of each unique essay (by prompt + draft_name combination)
  const latestDrafts = useMemo(() => {
    const draftMap = new Map();
    
    drafts.forEach(draft => {
      // Create a unique key based on draft name and prompt text
      const key = `${draft.draft_name || 'untitled'}-${draft.prompt_text?.substring(0, 100) || 'no-prompt'}`;
      
      // Keep only the latest version (highest created_at timestamp)
      if (!draftMap.has(key) || draft.created_at > draftMap.get(key).created_at) {
        draftMap.set(key, draft);
      }
    });
    
    return Array.from(draftMap.values()).sort((a, b) => b.created_at - a.created_at);
  }, [drafts]);


  // Transform university data to UI format
  const currentCollege = useMemo(() => {
    if (!selectedCollegeId || !allUniversities.length) return null;

    const university = allUniversities.find(u => u.university_id === selectedCollegeId);
    if (!university) return null;

    // Calculate days remaining (simplified calculation)
    const daysLeft = universityDetails?.days_remaining || 0;
    
    // Determine status colors based on university status
    const getStatusColors = (status: string, daysLeft: number) => {
      switch (status.toUpperCase()) {
        case 'COMPLETED':
          return {
            status: 'Submitted',
            statusColor: 'bg-green-100 text-green-700',
            daysLeft: 'Submitted',
            daysLeftColor: 'bg-green-100 text-green-700',
            progress: 100,
          };
        case 'IN_PROGRESS':
          return {
            status: 'In Progress',
            statusColor: 'bg-yellow-100 text-yellow-700',
            daysLeft: daysLeft > 0 ? `${daysLeft} days left` : 'Deadline passed',
            daysLeftColor: daysLeft > 7 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700',
            progress: 70,
          };
        default:
          return {
            status: 'Pending',
            statusColor: 'bg-gray-100 text-gray-700',
            daysLeft: daysLeft > 0 ? `${daysLeft} days left` : 'Not started',
            daysLeftColor: 'bg-gray-100 text-gray-700',
            progress: 10,
          };
      }
    };

    const statusInfo = getStatusColors(university.status, daysLeft);

    return {
      university_id: university.university_id,
      name: university.university_name,
      deadline: `Regular Decision • Deadline: ${daysLeft > 0 ? 'Upcoming' : 'Passed'}`,
      ...statusInfo,
      iconColor: 'bg-blue-100',
      iconTextColor: 'text-blue-600',
      interestStatus: 'Somewhat Considered',
      interestStatusColor: 'bg-yellow-100 text-yellow-700',
      interestDemonstrated: true,
      interestInfo: "Application progress is being tracked. Continue working on your essays and requirements.",
      interestInfoColor: 'bg-blue-50 border-blue-200',
      essayTitle: `${university.university_name} Supplemental Essay`,
    };
  }, [selectedCollegeId, allUniversities, universityDetails, drafts]);

  // Mock academic profile data - replace with actual API call when available
  const academicProfile = {
    gpa: {
      unweighted: 3.85,
      weighted: 4.2,
      classRank: 45,
      totalStudents: 423,
      percentile: 89,
    },
    testScores: {
      sat: {
        total: 1450,
        math: 720,
        reading: 730,
      },
      act: {
        composite: 32,
        english: 33,
        math: 34,
      },
    },
    apScores: [
      { name: 'Calculus BC', score: 5 },
      { name: 'Chemistry', score: 4 },
      { name: 'US History', score: 4 },
      { name: 'English Lang', score: 5 },
      { name: 'Physics C', score: 4 },
    ],
  };

  // Calculate AP average
  const apAverage = academicProfile.apScores.reduce((sum, ap) => sum + ap.score, 0) / academicProfile.apScores.length;

  const getInterestIconColor = (colorClass: string) => {
    if (colorClass.includes('green')) return 'text-green-600';
    if (colorClass.includes('yellow')) return 'text-yellow-600';
    return 'text-gray-600';
  };

  return (
    <div className="applications-page">
      <div className="applications-container">
        <header className="applications-header">
          <div className="header-content">
            <h1 className="applications-title">My Applications</h1>
            <div className="selector-group">
              <label className="selector-label">
                Select Application:
              </label>
              <select
                className="college-selector"
                value={selectedCollegeId || ''}
                onChange={(e) => setSelectedCollegeId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">All Applications</option>
                {allUniversities.map(university => (
                  <option key={university.university_id} value={university.university_id}>
                    {university.university_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </header>

        <div className="application-preview">
          {universitiesLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <div className="text-lg">Loading applications...</div>
              </div>
            </div>
          ) : !currentCollege ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No applications found. Add some universities to get started!</p>
            </div>
          ) : (
            <>
          {/* Application Header */}
          <div className="application-header-card">
            <div className="header-top">
              <div className="college-info">
                <div className={`college-icon ${currentCollege.iconColor}`}>
                  <IoDocumentText className={`${currentCollege.iconTextColor} text-2xl`} />
                </div>
                <div>
                  <h2 className="college-name">{currentCollege.name}</h2>
                  <p className="college-deadline">{currentCollege.deadline}</p>
                  <div className="status-badges">
                    <span className={`status-badge ${currentCollege.statusColor}`}>
                      {currentCollege.status}
                    </span>
                    <span className={`days-left-badge ${currentCollege.daysLeftColor}`}>
                      {currentCollege.daysLeft}
                    </span>
                  </div>
                </div>
              </div>
              <div className="header-actions">
                <button className="btn-primary">
                  <IoConstruct className="mr-2" />
                  <span>AI Review</span>
                </button>
                <button className="btn-secondary">
                  <IoCreate className="mr-2" />
                  <span>Edit</span>
                </button>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="progress-section">
              <div className="progress-header">
                <span className="progress-label">Overall Progress</span>
                <span className="progress-percentage">{currentCollege.progress}%</span>
              </div>
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${currentCollege.progress}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Demonstrated Interest Section */}
          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">
                Demonstrated Interest
              </h2>
              <IoHeart className="text-primary text-xl" />
            </div>
            <div className="interest-content">
              <div className="interest-info-group">
                <div className="interest-item">
                  <span className="interest-label">Status:</span>
                  <span className={`interest-status-badge ${currentCollege.interestStatusColor}`}>
                    {currentCollege.interestStatus}
                  </span>
                </div>
                <div className="interest-item">
                  <span className="interest-label">Demonstrated:</span>
                  {currentCollege.interestDemonstrated ? (
                    <>
                      <IoCheckmarkCircle className="text-green-600 text-lg" />
                      <span className="interest-value">Yes</span>
                    </>
                  ) : (
                    <>
                      <IoCloseCircle className="text-red-600 text-lg" />
                      <span className="interest-value">No</span>
                    </>
                  )}
                </div>
              </div>
              <a href="#" className="view-opportunities-link">
                <span>View opportunities</span>
                <IoArrowForward className="text-xs" />
              </a>
            </div>
            <div className={`interest-info-box ${currentCollege.interestInfoColor}`}>
              <p className="interest-info-text">
                <IoInformationCircle className={`${getInterestIconColor(currentCollege.interestInfoColor)} mr-1`} />
                {currentCollege.interestInfo}
              </p>
            </div>
          </div>

          {/* Academic Profile Section */}
          <div className="section-card">
            <h2 className="section-title mb-6">
              Academic Profile
            </h2>

            <div className="academic-grid">
              {/* GPA Card */}
              <div className="academic-card">
                <div className="academic-card-header">
                  <h3 className="academic-card-title">GPA Overview</h3>
                  <IoStatsChart className="text-primary" />
                </div>
                <div className="academic-card-content">
                  <div className="gpa-item">
                    <div className="gpa-header">
                      <span className="gpa-label">Unweighted GPA</span>
                      <span className="gpa-value">{academicProfile.gpa.unweighted.toFixed(2)}</span>
                    </div>
                    <div className="progress-bar-small">
                      <div className="progress-fill-primary" style={{ width: `${(academicProfile.gpa.unweighted / 4.0) * 100}%` }}></div>
                    </div>
                  </div>
                  <div className="gpa-item">
                    <div className="gpa-header">
                      <span className="gpa-label">Weighted GPA</span>
                      <span className="gpa-value">{academicProfile.gpa.weighted.toFixed(2)}</span>
                    </div>
                    <div className="progress-bar-small">
                      <div className="progress-fill-green" style={{ width: `${(academicProfile.gpa.weighted / 5.0) * 100}%` }}></div>
                    </div>
                  </div>
                  <div className="gpa-stats">
                    <div className="stat-row">
                      <span className="stat-label">Class Rank</span>
                      <span className="stat-value">{academicProfile.gpa.classRank} / {academicProfile.gpa.totalStudents}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Percentile</span>
                      <span className="stat-value-green">Top {100 - academicProfile.gpa.percentile}%</span>
                    </div>
                  </div>
                </div>
                <div className="info-box-blue">
                  <div className="info-box-header">
                    <div className="info-box-content">
                      <IoInformationCircle className="text-blue-600" />
                      <p className="info-text">
                        <span className="info-bold">GPA:</span> <span className="info-normal">Required</span> |{' '}
                        <span className="info-bold">Average GPA:</span> <span className="info-normal">3.75</span>
                      </p>
                    </div>
                  </div>
                  <p className="percentile-text">
                    <IoStatsChart />
                    You are in the <span className="percentile-value">75th percentile</span>
                  </p>
                </div>
              </div>

              {/* Test Scores Card */}
              <div className="academic-card">
                <div className="academic-card-header">
                  <h3 className="academic-card-title">Test Scores</h3>
                  <IoDocumentText className="text-primary" />
                </div>
                <div className="test-scores-content">
                  <div className="test-score-item">
                    <div className="test-score-header">
                      <span className="test-score-label">SAT</span>
                      <span className="test-score-value">{academicProfile.testScores.sat.total}</span>
                    </div>
                    <div className="test-score-breakdown">
                      <div className="breakdown-item">
                        <span className="breakdown-label">Math</span>
                        <p className="breakdown-value">{academicProfile.testScores.sat.math}</p>
                      </div>
                      <div className="breakdown-item">
                        <span className="breakdown-label">Reading</span>
                        <p className="breakdown-value">{academicProfile.testScores.sat.reading}</p>
                      </div>
                    </div>
                  </div>
                  <div className="test-score-divider"></div>
                  <div className="test-score-item">
                    <div className="test-score-header">
                      <span className="test-score-label">ACT</span>
                      <span className="test-score-value">{academicProfile.testScores.act.composite}</span>
                    </div>
                    <div className="test-score-breakdown">
                      <div className="breakdown-item">
                        <span className="breakdown-label">English</span>
                        <p className="breakdown-value">{academicProfile.testScores.act.english}</p>
                      </div>
                      <div className="breakdown-item">
                        <span className="breakdown-label">Math</span>
                        <p className="breakdown-value">{academicProfile.testScores.act.math}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="info-box-purple">
                  <div className="info-box-header">
                    <div className="info-box-content">
                      <IoInformationCircle className="text-purple-600" />
                      <p className="info-text">
                        <span className="info-bold">SAT/ACT:</span> <span className="info-normal">Strongly Recommended</span> |{' '}
                        <span className="info-bold">Average SAT:</span> <span className="info-normal">1400</span> |{' '}
                        <span className="info-bold">Average ACT:</span> <span className="info-normal">33</span>
                      </p>
                    </div>
                  </div>
                  <p className="percentile-text">
                    <IoStatsChart />
                    You are in the <span className="percentile-value">75th percentile</span>
                  </p>
                </div>
              </div>

              {/* AP Scores Card */}
              <div className="academic-card">
                <div className="academic-card-header">
                  <h3 className="academic-card-title">AP Scores</h3>
                  <IoMedal className="text-primary" />
                </div>
                <div className="ap-scores-list">
                  {academicProfile.apScores.map((ap, index) => (
                    <div 
                      key={ap.name} 
                      className={index === academicProfile.apScores.length - 1 ? "ap-score-item-last" : "ap-score-item"}
                    >
                      <span className="ap-score-name">{ap.name}</span>
                      <span className={ap.score === 5 ? "ap-score-badge-green" : "ap-score-badge-blue"}>
                        {ap.score}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="ap-average">
                  <p className="ap-average-text">
                    Average Score: <span className="ap-average-value">{apAverage.toFixed(1)}</span>
                  </p>
                </div>
                <div className="info-box-amber">
                  <div className="info-box-header">
                    <div className="info-box-content">
                      <IoInformationCircle className="text-amber-600" />
                      <p className="info-text">
                        <span className="info-bold">AP Scores:</span> <span className="info-normal">Recommended</span> |{' '}
                        <span className="info-bold">Average Score:</span> <span className="info-normal">4.2</span>
                      </p>
                    </div>
                  </div>
                  <p className="percentile-text">
                    <IoStatsChart />
                    You are in the <span className="percentile-value">75th percentile</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Activities Section */}
          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">
                Extracurricular Activities
              </h2>
              <button className="section-action-btn">
                <IoAdd className="mr-1" />
                Add Activity
              </button>
            </div>

            <div className="activities-grid">
              <div className="activity-card">
                <div className="activity-header">
                  <div className="activity-info">
                    <div className="activity-icon-blue">
                      <IoFlask className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="activity-title">Science Olympiad</h3>
                      <p className="activity-subtitle">Team Captain • 4 years</p>
                    </div>
                  </div>
                  <button className="activity-menu-btn">
                    <IoEllipsisVertical />
                  </button>
                </div>
                <p className="activity-description">
                  Led team of 15 students to regional championships. Competed in Chemistry Lab and Disease Detectives events.
                </p>
                <div className="activity-tags">
                  <span className="tag-blue">Leadership</span>
                  <span className="tag-purple">STEM</span>
                </div>
              </div>

              <div className="activity-card">
                <div className="activity-header">
                  <div className="activity-info">
                    <div className="activity-icon-green">
                      <IoHeartOutline className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="activity-title">Community Volunteer</h3>
                      <p className="activity-subtitle">Volunteer Coordinator • 3 years</p>
                    </div>
                  </div>
                  <button className="activity-menu-btn">
                    <IoEllipsisVertical />
                  </button>
                </div>
                <p className="activity-description">
                  Organized weekly tutoring sessions for underprivileged students. Accumulated 300+ volunteer hours.
                </p>
                <div className="activity-tags">
                  <span className="tag-green">Community Service</span>
                  <span className="tag-blue">Leadership</span>
                </div>
              </div>

              <div className="activity-card">
                <div className="activity-header">
                  <div className="activity-info">
                    <div className="activity-icon-purple">
                      <IoMusicalNotes className="text-purple-600" />
                    </div>
                    <div>
                      <h3 className="activity-title">School Orchestra</h3>
                      <p className="activity-subtitle">First Violin • 4 years</p>
                    </div>
                  </div>
                  <button className="activity-menu-btn">
                    <IoEllipsisVertical />
                  </button>
                </div>
                <p className="activity-description">
                  Performed in state competitions and local concerts. Selected for All-State Orchestra two consecutive years.
                </p>
                <div className="activity-tags">
                  <span className="tag-purple">Arts</span>
                  <span className="tag-yellow">Awards</span>
                </div>
              </div>

              <div className="activity-card">
                <div className="activity-header">
                  <div className="activity-info">
                    <div className="activity-icon-red">
                      <IoNewspaper className="text-red-600" />
                    </div>
                    <div>
                      <h3 className="activity-title">School Newspaper</h3>
                      <p className="activity-subtitle">Editor-in-Chief • 2 years</p>
                    </div>
                  </div>
                  <button className="activity-menu-btn">
                    <IoEllipsisVertical />
                  </button>
                </div>
                <p className="activity-description">
                  Managed editorial team and published monthly issues. Won state journalism awards for investigative reporting.
                </p>
                <div className="activity-tags">
                  <span className="tag-blue">Leadership</span>
                  <span className="tag-red">Writing</span>
                </div>
              </div>
            </div>

            <div className="ai-review-banner">
              <IoBulb className="text-blue-600 text-xl" />
              <div className="banner-content">
                <h4 className="banner-title">AI Review Available</h4>
                <p className="banner-description">Get personalized feedback on your activities list from our AI counselor</p>
              </div>
              <button className="banner-btn">Review Now</button>
            </div>
          </div>

          {/* Essays Section */}
          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">
                Essays & Writing Supplements
              </h2>
              <button className="section-action-btn">
                <IoAdd className="mr-1" />
                Start New Essay
              </button>
            </div>

            <div className="essays-list">
              {latestDrafts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <IoDocumentText className="mx-auto mb-2 text-3xl" />
                  <p>No essays found. Start writing your first essay!</p>
                </div>
              ) : (
                latestDrafts.map((draft, index) => {
                  const progress = draft.word_count > 0 ? Math.min(100, Math.round((draft.word_count / 650) * 100)) : 0;
                  const isComplete = draft.draft_status === 'COMPLETED';
                  const createdDate = new Date(draft.created_at * 1000).toLocaleDateString();
                  
                  // Create essay titles based on actual data only
                  const getEssayTitle = () => {
                    // Use actual draft name if available
                    if (draft.draft_name && draft.draft_name.trim()) {
                      return draft.draft_name;
                    }
                    
                    // If we have a prompt, use its beginning as title
                    if (draft.prompt_text && draft.prompt_text.length > 10) {
                      return `Essay: ${draft.prompt_text.substring(0, 50)}...`;
                    }
                    
                    // Fallback to basic identification
                    if (currentCollege) {
                      return `${currentCollege.name} Essay ${index + 1}`;
                    }
                    
                    return `Essay ${index + 1}`;
                  };

                  // Get a meaningful prompt display
                  const getPromptDisplay = () => {
                    if (draft.prompt_text && draft.prompt_text.trim()) {
                      return draft.prompt_text.length > 100 
                        ? `${draft.prompt_text.substring(0, 100)}...`
                        : draft.prompt_text;
                    }
                    return "Prompt not available - please check with your counselor";
                  };
                  
                  return (
                    <div key={draft.id} className="essay-card">
                      <div className="essay-header">
                        <div className="essay-content">
                          <div className="essay-title-row">
                            <h3 className="essay-title">{getEssayTitle()}</h3>
                            <span className={isComplete ? "essay-status-badge-green" : "essay-status-badge-yellow"}>
                              {isComplete ? 'Complete' : 'In Progress'}
                            </span>
                          </div>
                          <p className="essay-prompt">
                            Prompt: {getPromptDisplay()}
                          </p>
                          <div className="essay-meta">
                            <span>
                              <IoDocumentText className="mr-1" />
                              {draft.word_count} words
                            </span>
                            <span>
                              <IoTime className="mr-1" />
                              Last edited: {createdDate}
                            </span>
                            <span>
                              <IoTime className="mr-1" />
                              {isComplete ? 'Complete' : 'Draft'}
                            </span>
                          </div>
                        </div>
                        <div className="essay-actions">
                          <button className="essay-action-btn-primary">
                            <IoCreate />
                          </button>
                          <button className="essay-action-btn-secondary">
                            <IoDownload />
                          </button>
                        </div>
                      </div>
                      <div className="essay-progress">
                        <div className="essay-progress-bar">
                          <div 
                            className={isComplete ? "essay-progress-fill-green" : "essay-progress-fill-yellow"} 
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        <span className={isComplete ? "essay-progress-text-green" : "essay-progress-text-yellow"}>
                          {progress}%
                        </span>
                      </div>
                      {isComplete ? (
                        <div className="essay-ai-feedback">
                          <IoConstruct className="text-green-600 mr-2" />
                          <span>
                            <strong>Essay Complete</strong> - Review and make any final adjustments before submission.
                          </span>
                        </div>
                      ) : (
                        <div className="essay-actions-bottom">
                          <button className="essay-ai-btn">
                            <IoConstruct className="mr-2" />
                            Get AI Review
                          </button>
                          <button className="essay-continue-btn">Continue Writing</button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Separator */}
          <div className="section-separator">
            <div className="separator-line"></div>
            <div className="separator-text">Documents & Recommendations</div>
            <div className="separator-line"></div>
          </div>

          {/* Documents & Recommendations Section */}
          <div className="documents-section">
            <div className="documents-grid">
              {/* Recommendations Card */}
              <div className="section-card">
                <div className="section-header">
                  <h2 className="section-title">Letters of Recommendation</h2>
                  <button className="section-action-btn">
                    <IoAdd className="mr-1" />
                    Request New
                  </button>
                </div>

                <div className="recommendations-list">
                  <div className="recommendation-item">
                    <div className="recommendation-header">
                      <div className="recommendation-info">
                        <img
                          alt="Teacher"
                          className="recommendation-avatar"
                          src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg"
                        />
                        <div>
                          <h3 className="recommendation-name">Dr. Robert Chen</h3>
                          <p className="recommendation-role">AP Chemistry Teacher</p>
                        </div>
                      </div>
                      <span className="recommendation-status-green">Submitted</span>
                    </div>
                    <div className="recommendation-meta">
                      <span>
                        <IoCalendar className="mr-1" />
                        Submitted: Dec 20, 2024
                      </span>
                      <span>
                        <IoSchool className="mr-1" />
                        For 5 schools
                      </span>
                    </div>
                  </div>

                  <div className="recommendation-item">
                    <div className="recommendation-header">
                      <div className="recommendation-info">
                        <img
                          alt="Teacher"
                          className="recommendation-avatar"
                          src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-5.jpg"
                        />
                        <div>
                          <h3 className="recommendation-name">Ms. Jennifer Martinez</h3>
                          <p className="recommendation-role">English Literature Teacher</p>
                        </div>
                      </div>
                      <span className="recommendation-status-yellow">Pending</span>
                    </div>
                    <div className="recommendation-meta">
                      <span>
                        <IoTime className="mr-1" />
                        Requested: Dec 15, 2024
                      </span>
                      <span>
                        <IoSchool className="mr-1" />
                        For 8 schools
                      </span>
                    </div>
                    <button className="reminder-btn">Send Reminder</button>
                  </div>

                  <div className="recommendation-item">
                    <div className="recommendation-header">
                      <div className="recommendation-info">
                        <img
                          alt="Counselor"
                          className="recommendation-avatar"
                          src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-3.jpg"
                        />
                        <div>
                          <h3 className="recommendation-name">Mr. David Thompson</h3>
                          <p className="recommendation-role">School Counselor</p>
                        </div>
                      </div>
                      <span className="recommendation-status-green">Submitted</span>
                    </div>
                    <div className="recommendation-meta">
                      <span>
                        <IoCalendar className="mr-1" />
                        Submitted: Dec 18, 2024
                      </span>
                      <span>
                        <IoSchool className="mr-1" />
                        For all schools
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Documents Card */}
              <div className="section-card">
                <div className="section-header">
                  <h2 className="section-title">
                    Required Documents
                  </h2>
                  <button className="section-action-btn">
                    <IoAdd className="mr-1" />
                    Upload New
                  </button>
                </div>

                <div className="documents-list">
                  <div className="document-item-green">
                    <div className="document-info">
                      <div className="document-icon-green">
                        <IoFileTray className="text-white" />
                      </div>
                      <div>
                        <h3 className="document-name">Official Transcript</h3>
                        <p className="document-meta">Uploaded Dec 10, 2024 • 2.4 MB</p>
                      </div>
                    </div>
                    <IoCheckmarkCircle className="text-green-600 text-xl" />
                  </div>

                  <div className="document-item-green">
                    <div className="document-info">
                      <div className="document-icon-green">
                        <IoFileTray className="text-white" />
                      </div>
                      <div>
                        <h3 className="document-name">SAT Score Report</h3>
                        <p className="document-meta">Uploaded Dec 12, 2024 • 1.1 MB</p>
                      </div>
                    </div>
                    <IoCheckmarkCircle className="text-green-600 text-xl" />
                  </div>

                  <div className="document-item-green">
                    <div className="document-info">
                      <div className="document-icon-green">
                        <IoFileTray className="text-white" />
                      </div>
                      <div>
                        <h3 className="document-name">ACT Score Report</h3>
                        <p className="document-meta">Uploaded Dec 12, 2024 • 987 KB</p>
                      </div>
                    </div>
                    <IoCheckmarkCircle className="text-green-600 text-xl" />
                  </div>

                  <div className="document-item-yellow">
                    <div className="document-info">
                      <div className="document-icon-yellow">
                        <IoFileTray className="text-white" />
                      </div>
                      <div>
                        <h3 className="document-name">Mid-Year Report</h3>
                        <p className="document-meta">Required by Feb 1, 2025</p>
                      </div>
                    </div>
                    <button className="document-upload-btn">Upload</button>
                  </div>

                  <div className="document-item-gray">
                    <div className="document-info">
                      <div className="document-icon-gray">
                        <IoFileTray className="text-white" />
                      </div>
                      <div>
                        <h3 className="document-name">Financial Aid Documents</h3>
                        <p className="document-meta">Optional</p>
                      </div>
                    </div>
                    <button className="document-upload-btn-gray">Upload</button>
                  </div>
                </div>

                <div className="document-tips">
                  <IoInformationCircle className="text-blue-600 text-lg" />
                  <div>
                    <h4 className="tips-title">Document Tips</h4>
                    <ul className="tips-list">
                      <li>• All documents should be in PDF format</li>
                      <li>• Maximum file size: 5 MB per document</li>
                      <li>• Ensure all pages are clearly legible</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Applications;